import { HttpErrorResponse } from "@angular/common/http";
import {
	AfterViewInit,
	Component,
	computed,
	inject,
	OnInit,
	signal,
	TemplateRef,
	viewChild,
} from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { Language } from "@data/interfaces/shared/language";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { CacheService } from "@shared/services/cache.service";
import {
	ChangeData,
	CountryISO,
	NgxIntlTelInputModule,
	PhoneNumberFormat,
} from "ngx-intl-tel-input";
import { environment } from "src/environments/environment";
import languagesConfig from "src/language/Languages.json";
import { ContactService } from "../../../../services/contact.service";
import { ProfileService } from "../../../../services/profile/profile.service";
import { RelativeService } from "../../../../services/relative.service";
import { UserService } from "../../../../services/user.service";
import { UserStorageService } from "../../../../services/user-storage.service";
import { NavbarComponent } from "../../../layout/components/navbar/navbar.component";
import { isPasswordStrong } from "../../../shared/utils/password.util";
import { countryCodes } from "../country-codes";

export type Relative = {
	id: string;
	isNew: boolean;
	saving: boolean;
	name: string;
	surname: string;
	phone: ChangeData;
	mail: string;
	accessAllData: boolean;
	accessBooking: boolean;
	accessMyHealth: boolean;
	accessMyService: boolean;
	validationAttempted?: boolean;
	error?: string;
};

@Component({
	selector: "app-profile",
	templateUrl: "./profile.component.html",
	styleUrls: ["./profile.component.scss"],
	imports: [NavbarComponent, TranslatePipe, FormsModule, NgxIntlTelInputModule],
})
export class ProfileComponent implements OnInit, AfterViewInit {
	private userStorageService = inject(UserStorageService);
	private profileService = inject(ProfileService);
	private relativeService = inject(RelativeService);
	private contactService = inject(ContactService);
	private userService = inject(UserService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private modalService = inject(NgbModal);
	readonly #cacheService = inject(CacheService);

	readonly updatedDataModalTemplate =
		viewChild.required<TemplateRef<any>>("updatedDataModal");
	readonly deleteDataModalTemplate =
		viewChild.required<TemplateRef<any>>("deleteDataModal");
	readonly changePasswordModalTemplate = viewChild.required<TemplateRef<any>>(
		"changePasswordModal",
	);
	private changePasswordModalRef?: NgbModalRef;

	readonly isNativePlatform = computed(() => Capacitor.isNativePlatform());

	lang = this.userStorageService.getLanguage();
	selectedLanguage = this.lang?.shortcode ?? "en";
	public translate: TranslateService;
	firstName: string = "";
	surname: string = "";
	email: string = "";
	phone: ChangeData = {};
	sex: string = "Unspecified";
	birthday: string = "";
	street: string = "";
	city: string = "";
	state: string = "";
	zipCode: string = "";
	countryCode: string = "";
	countryCodes = countryCodes;
	protected readonly CountryISO = CountryISO;
	// ngx-intl-tel-input wants CountryISO; phone models carry the code as a plain lowercase string.
	protected countryIso(code: string | undefined | null): CountryISO {
		return (code?.toLowerCase() as CountryISO) || CountryISO.Norway;
	}
	onlyCountries: CountryISO[] = [
		CountryISO.Denmark,
		CountryISO.Sweden,
		CountryISO.Norway,
		CountryISO.Finland,
	];
	PhoneNumberFormat = PhoneNumberFormat;
	currentPassword: string = "";
	newPassword: string = "";
	confirmNewPassword: string = "";
	changingPassword: boolean = false;
	updated_modal_title: string = "";
	updated_modal_text: string = "";

	readonly languages = computed<Language[]>(() =>
		environment.SupportedLanguages.map((supportedLanguage) =>
			(languagesConfig as Language[]).find(
				(lang) => lang.shortcode === supportedLanguage,
			),
		).filter((lang): lang is Language => lang !== undefined),
	);

	readonly relatives = signal<Relative[]>([]);

	constructor() {
		const translate = inject(TranslateService);
		this.translate = translate;
	}

	ngOnInit(): void {
		this.setProfileInformations();
		this.loadProfileFromApi();
		this.loadRelatives();
	}

	// Deep-link target for the "forgot password" e-mail flow: the reset e-mail sends the user
	// to /profile?openChangePassword=1 (behind the auth guard) so that after they log in with
	// the auto-generated password, they land here and are immediately prompted to replace it
	// with one of their own choosing via the same self-service modal. Deferred to
	// ngAfterViewInit because the modal's TemplateRef isn't available until the view exists.
	ngAfterViewInit(): void {
		if (this.route.snapshot.queryParamMap.get("openChangePassword")) {
			// Deferred a tick so NgbModal doesn't open mid change-detection cycle.
			setTimeout(() => this.showChangePasswordModal());
			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: {},
				replaceUrl: true,
			});
		}
	}

	// The legacy identity object behind setProfileInformations() can be incomplete (e.g. missing
	// phone/email) for accounts whose real, complete record only exists in salubi-api. Overlay
	// that complete data on top once it loads. This is also what Update Profile now writes back
	// to, so reads and writes hit the same record.
	private loadProfileFromApi(): void {
		this.contactService.getSelf().subscribe({
			next: (user) => {
				if (
					user.language &&
					this.languages().some((lang) => lang.shortcode === user.language)
				) {
					this.selectedLanguage = user.language;
					this.userStorageService.setLanguage(
						this.getLanguageByCode(user.language),
					);
				}
				if (user.firstName) this.firstName = user.firstName;
				if (user.lastName) this.surname = user.lastName;
				if (user.emailAddress) this.email = user.emailAddress;
				if (user.sex) this.sex = user.sex;
				if (user.birthday) this.birthday = user.birthday.substring(0, 10);

				if (user.address) {
					this.street = user.address.street ?? "";
					this.city = user.address.city ?? "";
					this.state = user.address.state ?? "";
					this.zipCode = user.address.zipCode ?? "";
					this.countryCode = user.address.country?.iso2 ?? "";
				}

				if (user.phoneNumber?.number) {
					const iso = user.phoneNumber.countryCode
						? countryCodes.find(
								(c) => c.code === `+${user.phoneNumber?.countryCode}`,
							)?.country_code
						: undefined;

					this.phone = {
						...this.phone,
						number: user.phoneNumber.number,
						countryCode: iso?.toLowerCase() ?? this.phone?.countryCode,
					};
				}
			},
			error: () => {
				/* fall back to whatever setProfileInformations() already populated */
			},
		});
	}

	private loadRelatives(afterLoad?: () => void): void {
		this.relativeService.getRelatives().subscribe({
			next: (dtos) => {
				// Keep unsaved rows the user is still filling in.
				const unsaved = this.relatives().filter((r) => r.isNew);
				this.relatives.set([
					...dtos.map((dto) => this.toRelativeRow(dto)),
					...unsaved,
				]);
				afterLoad?.();
			},
			error: () => {
				/* profile still usable without relatives */
			},
		});
	}

	private toRelativeRow(dto: {
		relativeUserId: string;
		contact: {
			firstName: string | null;
			lastName: string | null;
			email: { address: string } | null;
			phoneNumber: {
				countryCode: string | null;
				number: string | null;
			} | null;
		} | null;
		allAccessData: boolean;
		bookingAccess: boolean;
		myHealthAccess: boolean;
		serviceAccess: boolean;
	}): Relative {
		return {
			id: dto.relativeUserId,
			isNew: false,
			saving: false,
			name: dto.contact?.firstName ?? "",
			surname: dto.contact?.lastName ?? "",
			phone: {
				countryCode: this.toCountryCode(
					dto.contact?.phoneNumber?.countryCode,
				).toLowerCase(),
				number: dto.contact?.phoneNumber?.number ?? "",
			},
			mail: dto.contact?.email?.address ?? "",
			accessAllData: dto.allAccessData,
			accessBooking: dto.bookingAccess,
			accessMyHealth: dto.myHealthAccess,
			accessMyService: dto.serviceAccess,
			error: undefined,
		};
	}

	private toCountryCode(phoneCountryCode: string | null | undefined): string {
		return (
			countryCodes.find((c) => c.code === `+${phoneCountryCode}`)
				?.country_code ??
			this.phone?.countryCode?.toUpperCase() ??
			"NO"
		);
	}

	private getPhoneCountryCode(countryCode: string): string {
		const found = countryCodes.find(
			(c) => c.country_code === countryCode.toUpperCase(),
		);
		return (found?.code ?? "+47").replace("+", "");
	}

	updateProfile = (profileForm: NgForm) => {
		if (profileForm.invalid) {
			profileForm.control.markAllAsTouched();
			return;
		}

		this.contactService
			.updateSelf({
				firstName: this.firstName,
				middleName: "",
				lastName: this.surname,
				sex: this.sex,
				birthday: this.birthday,
				phoneCountryCode: this.getPhoneCountryCode(
					this.phone?.countryCode ?? "NO",
				),
				phoneAreaCode: "",
				phoneNumber: this.phone.number ?? "",
				street: this.street,
				city: this.city,
				state: this.state,
				zipCode: this.zipCode,
				countryCode: this.countryCode,
				language: this.selectedLanguage,
			})
			.subscribe({
				next: () => {
					this.userService.setName(this.firstName);
					this.userService.setSurname(this.surname);
					this.updated_modal_text = this.translate.instant("Profile_Updated");
					this.updated_modal_title = this.translate.instant("Profile");
					this.showUpdatedDataModal();
				},
				error: () => {
					this.updated_modal_text = this.translate.instant(
						"Profile_Updated_Not",
					);
					this.updated_modal_title = this.translate.instant("Profile");
					this.showUpdatedDataModal();
				},
			});
	};

	public readonly isPasswordStrong = isPasswordStrong;

	public passwordsMatch(): boolean {
		return this.newPassword === this.confirmNewPassword;
	}

	changePassword = () => {
		if (
			!this.currentPassword ||
			!this.isPasswordStrong(this.newPassword) ||
			!this.passwordsMatch()
		) {
			return;
		}

		this.changingPassword = true;
		this.contactService
			.changePassword({
				oldPassword: this.currentPassword,
				newPassword: this.newPassword,
			})
			.subscribe({
				next: () => {
					this.changingPassword = false;
					this.currentPassword = "";
					this.newPassword = "";
					this.confirmNewPassword = "";
					this.changePasswordModalRef?.close();
					this.updated_modal_text = this.translate.instant("Password_Updated");
					this.updated_modal_title = this.translate.instant("Password");
					this.showUpdatedDataModal();
				},
				error: () => {
					this.changingPassword = false;
					this.updated_modal_text = this.translate.instant(
						"Password_Updated_Not",
					);
					this.updated_modal_title = this.translate.instant("Password");
					this.showUpdatedDataModal();
				},
			});
	};

	private showUpdatedDataModal(): void {
		this.modalService.open(this.updatedDataModalTemplate(), {
			ariaLabelledBy: "updated-data-modal-title",
			centered: true,
			backdrop: "static",
			keyboard: false,
		});
	}

	public showDeleteDataModal(): void {
		this.modalService.open(this.deleteDataModalTemplate(), {
			ariaLabelledBy: "delete-data-modal-title",
			centered: true,
			backdrop: "static",
			keyboard: false,
		});
	}

	public showChangePasswordModal(): void {
		this.currentPassword = "";
		this.newPassword = "";
		this.confirmNewPassword = "";
		this.changePasswordModalRef = this.modalService.open(
			this.changePasswordModalTemplate(),
			{
				ariaLabelledBy: "change-password-modal-title",
				centered: true,
			},
		);
	}

	deleteProfile = () => {
		this.profileService.closeProfile().then((closed: boolean) => {
			if (closed) {
				// Close any open modals
				this.modalService.dismissAll();
			}
		});
	};

	setProfileInformations = () => {
		this.phone.countryCode = "no";

		const user = this.userService.getUser();
		if (user) {
			this.applyUserToForm(user);
			return;
		}

		// /users/self is loaded separately and is the sole remote source for this form.
		// Do not start the legacy request here: it can finish after /users/self and overwrite
		// its complete contact data with an older, partial identity response.
	};

	private applyUserToForm(user: any): void {
		if (user.Firstname) this.firstName = user.Firstname;
		if (user.Lastname) this.surname = user.Lastname;
		if (user.Email) this.email = user.Email;

		if (user.PhoneNumbers?.length > 0) {
			const raw = user.PhoneNumbers[0].Countrycode?.toLowerCase();
			this.phone.countryCode =
				(raw === "nb" ? "no" : raw) ?? this.phone.countryCode;
			this.phone.number = user.PhoneNumbers[0].Number;
		}
	}

	public getLanguageByCode(shortcode: string): Language {
		return (
			this.languages().find((lang) => lang.shortcode === shortcode) ??
			this.languages()[0]
		);
	}

	public selectLanguage(lang: Language): void {
		this.selectedLanguage = lang.shortcode;
		this.userStorageService.setLanguage(lang);
		this.translate.use(lang.shortcode);
	}

	public addRelative(): void {
		this.relatives.update((relatives) => [
			...relatives,
			{
				id: crypto.randomUUID(),
				isNew: true,
				saving: false,
				name: "",
				surname: "",
				phone: {
					countryCode: this.phone?.countryCode ?? CountryISO.Norway,
					number: "",
				},
				mail: "",
				accessAllData: false,
				accessBooking: false,
				accessMyHealth: false,
				accessMyService: false,
				error: undefined,
			},
		]);
	}

	// Matches the backend's own phone number validation (CreateRelativeValidator: ^\d+$).
	public isPhoneValid(phone: ChangeData): boolean {
		return /^\d+$/.test(String(phone?.number ?? ""));
	}

	public isEmailValid(email: string): boolean {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	}

	public saveRelative(id: string): void {
		const relative = this.relatives().find((r) => r.id === id);
		if (!relative) {
			return;
		}

		this.relatives.update((relatives) =>
			relatives.map((r) =>
				r.id === id ? { ...r, validationAttempted: true } : r,
			),
		);

		if (
			!relative.name ||
			!relative.surname ||
			!this.isEmailValid(relative.mail) ||
			!this.isPhoneValid(relative.phone)
		) {
			return;
		}

		this.relatives.update((relatives) =>
			relatives.map((r) => (r.id === id ? { ...r, saving: true } : r)),
		);

		this.relativeService
			.createRelative({
				firstName: relative.name,
				lastName: relative.surname,
				email: relative.mail,
				phoneCountryCode: this.getPhoneCountryCode(
					relative.phone?.countryCode?.toUpperCase() ?? "NO",
				),
				phoneNumber: String(relative.phone?.number ?? ""),
				allAccessData: relative.accessAllData,
				bookingAccess: relative.accessBooking,
				myHealthAccess: relative.accessMyHealth,
				serviceAccess: relative.accessMyService,
			})
			.subscribe({
				next: (created) => {
					this.relatives.update((relatives) =>
						relatives.map((r) =>
							r.id === id ? this.toRelativeRow(created) : r,
						),
					);
				},
				error: (error: HttpErrorResponse) => {
					const message =
						error.status === 409
							? this.translate.instant("Profile_Relative_Email_Exists")
							: this.translate.instant("Profile_Relative_Save_Failed");
					this.relatives.update((relatives) =>
						relatives.map((r) =>
							r.id === id ? { ...r, saving: false, error: message } : r,
						),
					);
				},
			});
	}

	public deleteRelative(id: string): void {
		const relative = this.relatives().find((r) => r.id === id);
		if (!relative) {
			return;
		}

		if (relative.isNew) {
			this.relatives.update((relatives) =>
				relatives.filter((r) => r.id !== id),
			);
			return;
		}

		this.relatives.update((relatives) =>
			relatives.map((r) => (r.id === id ? { ...r, error: undefined } : r)),
		);

		// Always reload from the server: the backend can error after the delete
		// already went through, so the response alone isn't reliable.
		this.relativeService.deleteRelative(id).subscribe({
			next: () => this.loadRelatives(),
			error: () =>
				// Only flag the row if it really survived the failed delete.
				this.loadRelatives(() =>
					this.relatives.update((relatives) =>
						relatives.map((r) =>
							r.id === id
								? {
										...r,
										error: this.translate.instant(
											"Profile_Relative_Delete_Failed",
										),
									}
								: r,
						),
					),
				),
		});
	}

	// AV-044: "All Data" is a master switch, not a 4th independent permission -
	// it stays in sync with (and can force) the other three so the state sent
	// to the backend is never contradictory (e.g. "All Data" on but Booking off).
	public toggleRelativeAccess(
		id: string,
		field:
			| "accessAllData"
			| "accessBooking"
			| "accessMyHealth"
			| "accessMyService",
	): void {
		this.relatives.update((relatives) =>
			relatives.map((r) => {
				if (r.id !== id) {
					return r;
				}

				if (field === "accessAllData") {
					const next = !r.accessAllData;
					return {
						...r,
						accessAllData: next,
						accessBooking: next,
						accessMyHealth: next,
						accessMyService: next,
					};
				}

				const updated = { ...r, [field]: !r[field] };
				updated.accessAllData =
					updated.accessBooking &&
					updated.accessMyHealth &&
					updated.accessMyService;
				return updated;
			}),
		);

		const relative = this.relatives().find((r) => r.id === id);
		if (!relative || relative.isNew) {
			return;
		}

		this.relativeService
			.updateRelativeAccess(id, {
				allAccessData: relative.accessAllData,
				bookingAccess: relative.accessBooking,
				myHealthAccess: relative.accessMyHealth,
				serviceAccess: relative.accessMyService,
			})
			// Reload either way so the toggles reflect what the server stored.
			.subscribe({
				next: () => this.loadRelatives(),
				error: () => this.loadRelatives(),
			});
	}

	public logout(): void {
		sessionStorage.clear();
		localStorage.clear();
		Preferences.clear();
		this.#cacheService.clearAll();
		this.router.navigate(["link/mobile"]);
	}

	back = () => {
		window.history.back();
	};
}
